import { Component, EventEmitter, OnDestroy, Output, inject, signal, viewChild } from '@angular/core';
import { brandColor } from '../../services/theme';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';

import {
  PaymentService,
  RazorpayCheckout,
  postToGateway,
  openGatewayWindow,
  GATEWAY_WINDOW_NAME,
} from '../../services/payment';
import { RazorpayService, RazorpaySuccess } from '../../services/razorpay';
import { MemberService } from '../../services/member';
import { ToastService } from '../../services/toast';
import { OtpStep } from '../member/otp-step';
import { ReceiptView } from '../payments/receipt/receipt-view';
import { CoiView } from '../policies/coi-view/coi-view';
import { MemberEmailDialog } from '../../components/member-email-dialog/member-email-dialog';

/** What a "Collect" button hands to the flow. `amount`/`kycStatus` are fetched if absent. */
export interface CollectTarget {
  id: number;
  name?: string | null;
  mobile?: string | null;
  email?: string | null;
  amount?: number | null;
  kycStatus?: string | null;
}

/**
 * Inline collect-payment flow.
 *
 * Drop one `<app-collect-payment-flow (paid)="...">` into a page and call `start(target)` from a
 * Collect button. If the member is KYC-verified it starts paying straight away; otherwise it shows
 * an OTP dialog first. On a verified payment it emits `(paid)` so the host can refresh its table.
 *
 * WHICH gateway is the server's decision, from the partner's assignment — Razorpay opens in place,
 * Generali redirects off-site. Only the Razorpay branch ever emits `(paid)`; the redirect branch
 * settles on the gateway's callback, and the customer returns to the payment result page instead.
 */
@Component({
  selector: 'app-collect-payment-flow',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, OtpStep, ReceiptView, CoiView, MemberEmailDialog],
  templateUrl: './collect-payment-flow.html',
  styleUrls: ['./collect-payment-flow.css'],
})
export class CollectPaymentFlow implements OnDestroy {
  private payment = inject(PaymentService);
  private rzp = inject(RazorpayService);
  private members = inject(MemberService);
  private toast = inject(ToastService);

  /** Emitted after a verified, successful payment — the host reloads its data. */
  @Output() paid = new EventEmitter<CollectTarget>();

  /** Opened when a gateway refuses the payment for want of an e-mail. */
  private emailDialog = viewChild(MemberEmailDialog);

  member = signal<CollectTarget | null>(null);
  starting = signal(false); // resolving member / creating the order (brief overlay)
  private processing = false; // overall payment in-flight (guards double starts)
  showOtp = signal(false);

  sendingOtp = signal(false);
  otpSent = signal(true);
  otpChannels = signal<string[]>([]);
  otpExpiry = signal(5);
  otpMessage = signal('');

  private paymentUid = '';
  private paymentDbId = 0; // numeric payment id from CreateOrder (receipt fallback)

  /** True while the customer is paying in the redirect gateway's popup. */
  awaitingGateway = signal(false);
  private gatewayWindow: Window | null = null;
  private pollTimer: any = null;

  // Each poll may make the server ask the gateway directly, so it is deliberately unhurried,
  // and capped so an abandoned window can't keep querying Generali indefinitely.
  private static readonly POLL_MS = 5000;
  private static readonly POLL_LIMIT_MS = 8 * 60 * 1000;

  // Post-payment success panel — offers the receipt + certificate the backend returns on PAID.
  showSuccess = signal(false);
  showReceipt = signal(false);
  showCoi = signal(false);
  receiptPaymentId = signal<number | null>(null);
  coiMemberId = signal<number | null>(null);

  /** Entry point from a Collect button. */
  start(t: CollectTarget) {
    if (!t?.id || this.processing) return;
    this.rzp.load(); // warm Checkout while we resolve/route
    if (t.kycStatus == null || t.amount == null) {
      this.resolveThenRoute(t); // fetch the member to learn KYC status / premium
    } else {
      this.member.set(t);
      this.route(t);
    }
  }

  /** Fill in missing KYC status / amount from the member record, then route. */
  private resolveThenRoute(t: CollectTarget) {
    this.starting.set(true);
    this.members.getById(t.id).subscribe({
      next: (res: any) => {
        const m = res?.data ?? {};
        const filled: CollectTarget = {
          id: t.id,
          name: t.name ?? this.nameOf(m),
          mobile: t.mobile ?? m?.mobileNumber ?? m?.MobileNumber ?? null,
          email: t.email ?? m?.email ?? m?.Email ?? null,
          amount: t.amount ?? this.premiumOf(m),
          kycStatus: t.kycStatus ?? m?.kycStatus ?? m?.KycStatus ?? 'NOT_VERIFIED',
        };
        this.starting.set(false);
        this.member.set(filled);
        this.route(filled);
      },
      error: () => {
        this.starting.set(false);
        this.toast.error('Could not load member details.');
      },
    });
  }

  private route(t: CollectTarget) {
    if (!t.amount || t.amount <= 0) {
      this.toast.warning('No premium amount to collect for this member.');
      return;
    }
    if ((t.kycStatus || '').toString().toUpperCase() === 'VERIFIED') this.pay();
    else this.beginOtp();
  }

  // ----- OTP (unverified members) -----
  private beginOtp() {
    this.showOtp.set(true);
    this.sendingOtp.set(true);
    this.members.resendOtp(this.member()!.id).subscribe({
      next: (res: any) => {
        this.sendingOtp.set(false);
        const d = res?.data ?? {};
        this.otpChannels.set(d?.otp?.channels ?? d?.channels ?? []);
        this.otpExpiry.set(d?.otp?.expiresInMinutes ?? d?.expiresInMinutes ?? 5);
        this.otpMessage.set(d?.otpMessage ?? res?.message ?? '');
        this.otpSent.set(res?.success !== false);
        if (res?.success === false) this.toast.error(res?.message || 'Could not send OTP.');
      },
      error: () => {
        this.sendingOtp.set(false);
        this.otpSent.set(false);
        this.toast.error('Could not send the OTP. Use “Resend” to try again.');
      },
    });
  }

  onVerified() {
    this.showOtp.set(false);
    this.pay();
  }

  // ----- pay (whichever gateway this partner is assigned) -----
  /**
   * The gateway is NOT chosen here.
   *
   * This used to POST straight to Razorpay, which meant assigning a partner the Generali Central
   * integration had no effect whatsoever — the choice had already been made in the browser. The
   * server now resolves it from the partner's assignment and tells us which flow came back.
   */
  private pay() {
    const t = this.member();
    if (!t || this.processing) return;
    this.processing = true;
    this.starting.set(true);

    // Opened NOW, not when the response lands: a window.open from an HTTP callback is
    // unsolicited and gets blocked. A redirect gateway is then driven inside it, which keeps this
    // page on screen behind the payment exactly as Razorpay's modal does. Razorpay doesn't need
    // it, so the window is closed again below if that is what comes back.
    this.gatewayWindow = openGatewayWindow();

    this.payment.payStart({ memberId: t.id }).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.closeGatewayWindow();

          // The gateway needs an e-mail this member doesn't have. Offer to add it here rather
          // than making someone leave a half-finished collection to go and edit the member.
          if (res?.data?.needsEmail) {
            this.processing = false;
            this.starting.set(false);
            this.emailDialog()?.open();
            return;
          }

          this.fail(res?.message || 'Could not start the payment.');
          return;
        }
        const d = res?.data ?? {};

        if (String(d.gateway || '').toUpperCase() === 'GENERALI') {
          this.openGenerali(d);
          return;
        }

        this.closeGatewayWindow(); // Razorpay draws its own overlay in this tab
        this.openCheckout(d as RazorpayCheckout);
      },
      error: () => {
        this.closeGatewayWindow();
        this.fail('Could not start the payment.');
      },
    });
  }

  /**
   * Generali is a REDIRECT gateway — the customer pays on Generali's own page. It cannot be put in
   * an iframe (payment pages refuse to be framed, and a blank modal is worse than an honest
   * redirect), so it runs in a popup instead: the app stays visible behind it and we watch the
   * payment settle, which gives the same in-place feel as Razorpay's modal.
   *
   * Whatever happens to this tab, the gateway's own callback confirms and issues server-side —
   * the polling below only decides what THIS screen shows.
   */
  private openGenerali(d: { postUrl?: string; fields?: Record<string, string>; paymentUniqueId?: string; paymentId?: number }) {
    const target = this.gatewayWindow ? GATEWAY_WINDOW_NAME : undefined;

    if (!postToGateway(d?.postUrl, d?.fields, target)) {
      this.closeGatewayWindow();
      this.fail('The payment gateway did not return a valid redirect.');
      return;
    }

    this.paymentUid = d.paymentUniqueId ?? '';
    this.paymentDbId = Number(d.paymentId) || 0;

    // Popup blocked: the form went to this tab instead and we are navigating away. Nothing left
    // to watch — the customer comes back via /payments/result.
    if (!this.gatewayWindow) return;

    this.starting.set(false);
    this.awaitingGateway.set(true);
    this.watchGatewayPayment();
  }

  /**
   * Watch the payment settle while the customer is in the popup.
   *
   * GetByPaymentUniqueId asks the gateway directly whenever the row is still pending, so each
   * poll both observes and repairs — a payment whose callback never arrived still lands here.
   * That makes a poll expensive at the gateway's end, hence the unhurried interval and the cap:
   * a partner who wandered off must not leave us querying Generali forever.
   */
  private watchGatewayPayment() {
    this.clearGatewayPoll();
    let elapsed = 0;

    this.pollTimer = setInterval(() => {
      elapsed += CollectPaymentFlow.POLL_MS;

      const closed = !!this.gatewayWindow?.closed;
      if (elapsed >= CollectPaymentFlow.POLL_LIMIT_MS) {
        this.clearGatewayPoll();
        this.checkGatewayPayment(true);
        return;
      }

      // Window shut: one final check, because it may have been closed a moment AFTER paying.
      if (closed) {
        this.clearGatewayPoll();
        this.checkGatewayPayment(true);
        return;
      }

      this.checkGatewayPayment(false);
    }, CollectPaymentFlow.POLL_MS);
  }

  /** One status read. `final` = stop watching and report whatever it says. */
  private checkGatewayPayment(final: boolean) {
    if (!this.paymentUid) {
      if (final) this.giveUpOnGateway();
      return;
    }

    this.payment.payGetByUid(this.paymentUid).subscribe({
      next: (res: any) => {
        const p = res?.data;
        const status = String(p?.paymentStatus ?? '').toUpperCase();

        if (status === 'SUCCESS') {
          this.clearGatewayPoll();
          this.closeGatewayWindow();
          this.onGatewaySuccess(Number(p?.id) || this.paymentDbId);
          return;
        }

        if (status === 'FAILED' || status === 'CANCELLED') {
          this.clearGatewayPoll();
          this.closeGatewayWindow();
          this.processing = false;
          this.awaitingGateway.set(false);
          this.toast.error(p?.failureReason || 'The payment was not completed.');
          return;
        }

        if (final) this.giveUpOnGateway();
      },
      error: () => {
        if (final) this.giveUpOnGateway();
      },
    });
  }

  /** Still unsettled when we stopped watching — never imply nothing was charged. */
  private giveUpOnGateway() {
    this.clearGatewayPoll();
    this.processing = false;
    this.awaitingGateway.set(false);
    this.toast.info(
      'The payment window closed before the payment was confirmed. If money was debited it will ' +
        'confirm shortly — do NOT collect again; check the member before retrying.',
    );
  }

  private onGatewaySuccess(paymentId: number) {
    this.processing = false;
    this.awaitingGateway.set(false);
    if (paymentId) this.receiptPaymentId.set(paymentId);
    const mid = this.member()?.id || 0;
    if (mid) this.coiMemberId.set(mid);
    this.showSuccess.set(true);
    this.toast.success(`${this.member()?.name || 'Member'} is now insured (PAID).`, 'Payment successful');
    this.paid.emit(this.member()!);
  }

  private clearGatewayPoll() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private closeGatewayWindow() {
    try {
      this.gatewayWindow?.close();
    } catch {
      /* already gone, or cross-origin after the gateway navigated it */
    }
    this.gatewayWindow = null;
  }

  /** The customer is mid-payment in the popup — bring them back to it. */
  focusGatewayWindow() {
    try {
      this.gatewayWindow?.focus();
    } catch {
      /* nothing to focus */
    }
  }

  /** The e-mail was saved — carry straight on with the payment that needed it. */
  onEmailSaved(email: string) {
    const t = this.member();
    if (t) this.member.set({ ...t, email });
    this.pay();
  }

  /** Stop paying for a payment nobody is watching any more. */
  ngOnDestroy() {
    this.clearGatewayPoll();
  }

  private openCheckout(co: RazorpayCheckout) {
    const t = this.member();
    if (!co?.orderId || !co?.keyId) {
      this.fail('Payment could not be initialised.');
      return;
    }
    this.paymentUid = co.paymentUniqueId;
    this.paymentDbId = co.paymentId;
    this.rzp.load().then((ok) => {
      if (!ok) {
        this.fail('Could not load Razorpay Checkout. Check your connection.');
        return;
      }
      this.starting.set(false); // hide our overlay — Razorpay's popup now takes over
      this.rzp.open({
        key: co.keyId,
        order_id: co.orderId,
        amount: co.amount,
        currency: co.currency,
        name: co.name,
        description: co.description,
        prefill: { name: t?.name, email: t?.email, contact: t?.mobile },
        theme: { color: brandColor() },
        handler: (r) => this.verify(r),
        modal: { ondismiss: () => this.onDismiss() },
        onFailed: (e) => this.fail(e?.error?.description || 'Payment failed.'),
      });
    });
  }

  private verify(r: RazorpaySuccess) {
    this.payment
      .rzpVerify({
        razorpayOrderId: r.razorpay_order_id,
        razorpayPaymentId: r.razorpay_payment_id,
        razorpaySignature: r.razorpay_signature,
      })
      .subscribe({
        next: (res: any) => {
          this.processing = false;
          this.starting.set(false);
          const ms = res?.data?.memberPaymentStatus ?? res?.data?.MemberPaymentStatus;
          if (res?.success && ms === 'PAID') {
            this.toast.success(`${this.member()?.name || 'Member'} is now insured (PAID).`, 'Payment successful');
            // Offer the receipt + certificate the backend hands back on PAID.
            const rid = Number(res?.data?.receipt?.paymentId ?? res?.data?.receipt?.PaymentId) || this.paymentDbId;
            if (rid) this.receiptPaymentId.set(rid);
            const mid = Number(res?.data?.coi?.memberId ?? res?.data?.coi?.MemberId) || this.member()?.id || 0;
            if (mid) this.coiMemberId.set(mid);
            this.showSuccess.set(true);
            this.paid.emit(this.member()!);
          } else {
            // Verify said no. The customer may still have been charged, so ask the gateway
            // before leaving this member looking UNPAID.
            this.reconcileAfterFailedVerify(r.razorpay_payment_id, res?.message);
          }
        },
        error: () => {
          this.processing = false;
          this.starting.set(false);
          this.reconcileAfterFailedVerify(r.razorpay_payment_id);
        },
      });
  }

  /**
   * The checkout handshake failed, but Razorpay may well be holding the money. Ask the server to
   * re-check: GET /MemberPayment/GetByPaymentUniqueId runs ReconcileIfPaid, which queries Razorpay
   * directly and confirms + issues if the payment was really captured. Only if that still comes
   * back unpaid do we show the "taken but unconfirmed" warning — otherwise the partner is told to
   * chase a payment that just settled itself, and is tempted to collect twice.
   */
  private reconcileAfterFailedVerify(gatewayPaymentId?: string, verifyMessage?: string) {
    const warn = () =>
      this.toast.error(
        gatewayPaymentId
          ? `Payment WAS taken (ref ${gatewayPaymentId}) but could not be confirmed. ` +
              'Do NOT collect again — quote this reference to support.'
          : verifyMessage || 'Payment verification failed.',
      );

    if (!this.paymentUid) {
      warn();
      return;
    }

    this.payment.payGetByUid(this.paymentUid).subscribe({
      next: (res: any) => {
        const st = (res?.data?.paymentStatus ?? '').toString().toUpperCase();
        if (st !== 'SUCCESS') {
          warn();
          return;
        }
        // It had settled after all — finish the normal success path.
        const rid = Number(res?.data?.id) || this.paymentDbId || 0;
        if (rid) this.receiptPaymentId.set(rid);
        const mid = this.member()?.id || 0;
        if (mid) this.coiMemberId.set(mid);
        this.showSuccess.set(true);
        this.toast.success('Payment confirmed.');
        this.paid.emit(this.member()!);
      },
      error: () => warn(),
    });
  }

  private onDismiss() {
    this.processing = false;
    this.starting.set(false);
    this.toast.info('Payment not completed. If money was debited it will confirm shortly.');
  }

  // ----- post-payment success panel -----
  openReceipt() {
    if (this.receiptPaymentId()) this.showReceipt.set(true);
  }
  openCoi() {
    if (this.coiMemberId()) this.showCoi.set(true);
  }

  private fail(msg: string) {
    this.processing = false;
    this.starting.set(false);
    this.toast.error(msg);
  }

  private premiumOf(m: any): number {
    for (const k of ['premiumWithGST', 'PremiumWithGST', 'grossAmt', 'GrossAmt']) {
      const v = Number(m?.[k]);
      if (Number.isFinite(v) && v > 0) return v;
    }
    return 0;
  }
  private nameOf(m: any): string {
    return (
      m?.memberName ||
      [m?.firstName, m?.middleName, m?.lastName].filter(Boolean).join(' ') ||
      m?.fullName ||
      ''
    );
  }
}
